/** tm/components/CompanyMatchFields.tsx — 정부지원사업 매칭 정보 입력 공용 컴포넌트
 *  RegisterClientModal · ConsultResultModal · CompanyDetailPanel · MatchFillPanel 공용 —
 *  매칭 7조건(지역·시군구·업종·기업규모·창업기업·대표나이·분류) + 전용분야(게이트) + 연매출·상시근로자수·업력.
 *  「N/9」 진행률의 9개 필수 필드 = 지역·업종·기업규모·창업기업·대표생년월일·상시근로자수·연매출·설립일·관심분류
 *  (시·군·구·전용분야는 보조 필드라 제외 — MatchFillPanel 표 열과 기준 동일). value/onChange 제어 컴포넌트.
 *  하위 업종 (2026-10-05g 「업종 → 하위 업종」): 업종 · 주요 제품 글로 자동 판정(점선 · 「자동」) — 누르면 빠지고 sectors_off 로 저장,
 *   자동으로 안 잡힌 분야는 눌러서 직접 켠다(키 'P›S' 를 arts 배열 = 업체 tags 에). 옛 전용 분야 · 업종 전용 체크는 불러올 때 새 키로 옮긴다.
 *  전용 대상: 기업 유형(확인서 · 매장운영/협동조합/농업법인 자동) + 특수 자격(재창업·재도전 · 재해 피해 · 넷제로 — 확인된 경우만 직접 체크).
 *   자동(점선) 유형 칩도 하위 업종처럼 누르면 «아님»(sectors_off 에 라벨 — 엔진이 자동으로 넣지 않는다) · 다시 누르면 자동으로 (2026-10-07h 지도사 「매장운영이 아닌데 지워지지도 않아」).
 *   직접 체크한 칩(arts)은 그대로 — 누르면 체크 해제. */
import React, { useEffect, useState } from 'react';
import DateInput from './DateInput';
import { C, T, R, REGION_CITIES } from './ui';
import * as api from '../api';
import { GUIDE as TARGET_GUIDE } from './GovTargetChips';
import { GovCategoryPicker, GOV_CATEGORIES as TAX_CATEGORIES, LEGACY_CAT, GOV_TYPES, GOV_SPECIAL_INFO, GOV_SPECIAL_Q, GOV_PARENTS, GOV_SUBS, GOV_SUB_INFO, LEGACY_TO_KEYS, LEGACY_SPECIAL, ANY_PARENT, isSubKey, parseSubKey, subKey, OLD_SUB_KEYS, canonSubKey } from './GovTaxonomy';

export interface MatchFieldsValue {
  region: string; region_detail: string;
  biz_types: string[]; biz_type: string;
  company_scale: string; is_startup: boolean | null;
  ceo_birth: string; employees: string; sales_eok: string; established_date: string;
  tags: string[]; arts: string[];
  /** 업종 글로 자동 해당한 하위 업종 키 · 자동 기업 유형 라벨(매장운영 등) 중 «아님»으로 뺀 것 (companies.sectors_off) */
  sectors_off: string[];
}

export const MATCH_REGIONS = ['서울','부산','대구','인천','광주','대전','울산','세종','경기','강원','충북','충남','전북','전남','경북','경남','제주'];
export const MATCH_BIZ_TYPES = GOV_PARENTS; // 2026-10-03 숙박/음식점업 → 둘로 · 2026-10-05g 농림축수산업 (엔진 PARENTS 한 벌)
export const MATCH_SCALES = ['소상공인','소기업','중기업','중소기업','중견기업'];   // 중기업 (2026-10-05q — 비워 두면 엔진이 업종 · 매출 · 상시근로자로 자동 판정 · 고르면 그것이 우선)
/** 로움 분류 목록 — GovPage.tsx / CONTRACT.md 와 동일해야 한다(엔진 매칭 어휘) */
/* 분류 체계는 GovTaxonomy.tsx 한 벌 — 옛 라벨(고용지원금 등)도 태그로 남아 있을 수 있어 매칭 판별에 포함 */
export const GOV_CATEGORIES = [...TAX_CATEGORIES, ...Object.keys(LEGACY_CAT)];
/** 옛 전용 분야 라벨 (2026-10-05g 전 — 업체 tags 에 남아 있을 수 있다. 불러올 때 하위 업종 키로 옮긴다) */
export const ARTS_CATEGORIES = ['예술', '스포츠', '식품', '환경', '문화', '화장품', '농업', '수산', '관광', '반려동물', '물산업', '핀테크', '서점'];
/** 기업 유형 — 폼 값은 arts 배열에 함께 담는다(저장은 둘 다 업체 tags) */
export const COMPANY_TYPES = GOV_TYPES;
/** 이 칸이 다루는 태그인가 — 하위 업종 키 · 기업 유형 · 특수 자격 · 옛 라벨 */
const isMatchTarget = (t: string) => isSubKey(t) || GOV_TYPES.includes(t) || GOV_SPECIAL_Q.includes(t) || !!LEGACY_TO_KEYS[t] || !!LEGACY_SPECIAL[t] || !!OLD_SUB_KEYS[t];
/** 옛 라벨 → 새 키 · 이름 (전용 분야 · 업종 전용 · 옛 특수 자격 이름) */
const upgradeTags = (list: string[]): string[] => {
  const out: string[] = [];
  // 옛 이름 키(「IT/SW개발업›핀테크」)는 새 이름(핀테크·블록체인)으로 (2026-10-05p)
  for (const t of list) for (const x of (LEGACY_TO_KEYS[t] || OLD_SUB_KEYS[t] || [LEGACY_SPECIAL[t] || canonSubKey(t)])) if (!out.includes(x)) out.push(x);   // 옛 키(바이오·제약·의료기기 …)도 새 키 둘로 (2026-10-05m)
  return out;
};

/** JSONB·문자열·배열 무엇으로 오든 문자열 배열로 (companies 목록 API가 tags 를 파싱하지 않는다) */
export function toStrArr(v: unknown): string[] {
  if (Array.isArray(v)) return v as string[];
  if (typeof v === 'string') { try { const p = JSON.parse(v || '[]'); return Array.isArray(p) ? p : []; } catch { return []; } }
  return [];
}

export function emptyMatchFields(): MatchFieldsValue {
  return { region: '', region_detail: '', biz_types: [], biz_type: '', company_scale: '', is_startup: null,
    ceo_birth: '', employees: '', sales_eok: '', established_date: '', tags: [], arts: [], sectors_off: [] };
}

/** 업체 행(Company 또는 그 일부) → 폼 값. tags 는 관심분류/전용분야로 갈라 담는다(나머지 자유 태그는 otherTagsOf로 별도 취급) */
export function matchFieldsFromCompany(c: {
  region?: string | null; region_detail?: string | null; biz_types?: unknown; biz_type?: string | null;
  company_scale?: string | null; is_startup?: boolean | null;
  ceo_birth?: string | null; employees?: number | null; sales_eok?: number | null; established_date?: string | null;
  tags?: unknown; sectors_off?: unknown;
}): MatchFieldsValue {
  const allTags = toStrArr(c.tags);
  const govSet = new Set(GOV_CATEGORIES);
  return {
    region: c.region || '', region_detail: c.region_detail || '',
    biz_types: toStrArr(c.biz_types), biz_type: c.biz_type || '',
    company_scale: c.company_scale || '', is_startup: c.is_startup == null ? null : !!c.is_startup,
    ceo_birth: c.ceo_birth ? String(c.ceo_birth).slice(0, 10) : '',
    employees: c.employees != null ? String(c.employees) : '',
    sales_eok: c.sales_eok != null ? String(c.sales_eok) : '',
    established_date: c.established_date ? String(c.established_date).slice(0, 10) : '',
    tags: allTags.filter(t => govSet.has(t)), arts: upgradeTags(allTags.filter(isMatchTarget)),
    sectors_off: upgradeTags(toStrArr(c.sectors_off)).filter(k => isSubKey(k) || GOV_TYPES.includes(k)),   // 기업 유형 라벨도 «아님» (2026-10-07h)
  };
}

/** 이 컴포넌트가 다루지 않는 자유 태그(다른 화면이 지정한 값) — 저장 시 합쳐 돌려놓아야 사라지지 않는다 */
export function otherTagsOf(tags: unknown): string[] {
  const govSet = new Set(GOV_CATEGORIES);
  return toStrArr(tags).filter(t => !govSet.has(t) && !isMatchTarget(t));
}
export function mergeTags(other: string[], v: Pick<MatchFieldsValue, 'tags' | 'arts'>): string[] { return [...other, ...v.tags, ...v.arts]; }

export interface MatchStat { label: string; filled: boolean }
/** 9개 필수 필드 결측 판정 — CompanyListPage 배지 · MatchFillPanel 열 · 이 컴포넌트 진행률이 같은 기준을 쓴다 */
export function matchStats(v: MatchFieldsValue): MatchStat[] {
  return [
    { label: '지역', filled: !!v.region },
    { label: '업종', filled: v.biz_types.length > 0 || v.biz_type.trim() !== '' },
    { label: '기업규모', filled: !!v.company_scale },
    { label: '창업기업 여부', filled: v.is_startup !== null },
    { label: '대표 생년월일', filled: !!v.ceo_birth },
    { label: '상시근로자수', filled: v.employees !== '' },
    { label: '연매출', filled: v.sales_eok !== '' },
    { label: '설립일', filled: !!v.established_date },
    { label: '관심 분류', filled: v.tags.length > 0 },
  ];
}
export function matchProgress(v: MatchFieldsValue): { filled: number; total: number; missing: string[] } {
  const s = matchStats(v);
  return { filled: s.filter(x => x.filled).length, total: s.length, missing: s.filter(x => !x.filled).map(x => x.label) };
}

// ── 표시 ────────────────────────────────────────────────────────
const lbl: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: C.sub, marginBottom: 5 };
const inp: React.CSSProperties = { width: '100%', minHeight: 36, padding: '8px 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: 13, outline: 'none', boxSizing: 'border-box' as const, background: '#fff' };
function need(ok: boolean) { return ok ? null : <span title="매칭에 필요" style={{ color: '#DC2626', marginLeft: 4, fontSize: 10 }}>●</span>; }
function Pill({ active, auto, dropped, onClick, children, bd, bg, fg, title }: { active: boolean; auto?: boolean; dropped?: boolean; onClick: () => void; children: React.ReactNode; bd: string; bg: string; fg: string; title?: string }) {
  // auto = 체크는 안 했지만 업종으로 자동 해당(점선 테두리 · 연한 바탕) — 놓치지 않게 보여만 준다 (2026-10-03)
  // dropped = 자동 해당했지만 «아님»으로 뺀 것(흐린 점선 · 글자 취소선은 호출하는 쪽 dropTxt) — 누르면 다시 자동으로 (2026-10-07h · 하위 업종 · 기업 유형 같은 모양)
  const on = active || !!auto;
  return (
    <span onClick={onClick} title={title} style={{
      padding: '7px 13px', minHeight: 34, borderRadius: R.sm, fontSize: 12.5, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, boxSizing: 'border-box' as const,
      border: `1px ${(!active && auto) || dropped ? 'dashed' : 'solid'} ${on ? bd : dropped ? '#D1D5DB' : C.border}`, background: active ? bg : auto ? `${bg}99` : dropped ? '#F9FAFB' : '#fff',
      color: on ? fg : dropped ? '#9CA3AF' : C.sub, fontWeight: active ? 700 : auto ? 600 : 400,
    }}>{children}</span>
  );
}
/** 칩 안의 짧은 뜻 · 「자동」 표시 · «아님»으로 뺀 이름(취소선) */
const subTxt: React.CSSProperties = { fontWeight: 400, opacity: 0.6 };
const autoMark: React.CSSProperties = { fontSize: 10, fontWeight: 700, padding: '0 4px', borderRadius: 3, background: '#fff', border: '1px solid currentColor', lineHeight: '14px', opacity: 0.85 };
const dropTxt: React.CSSProperties = { textDecoration: 'line-through', textDecorationThickness: '1px', opacity: 0.8 };
const DROPPED_TIP = '자동 해당했지만 «아님»으로 뺐습니다 — 누르면 다시 넣습니다';
interface Props {
  value: MatchFieldsValue;
  onChange: (patch: Partial<MatchFieldsValue>) => void;
  /** compact = 2열 그리드(넓은 화면), full = 1열(좁은 모달). 모바일은 항상 1열 */
  layout?: 'compact' | 'full';
  /** 업체 이름 — 협동조합·마을기업 등 이름으로 붙는 기업 유형 자동 판정용 (없어도 됨) */
  companyName?: string;
  /** 저장된 세부업종 AI 판정(companies.gov_ai · 2026-10-07k) — 지금 업종 글과 같으면 글 규칙 대신 이것이 자동 칩 · 글이 바뀌면 저장 뒤 다시 판정 */
  govAi?: unknown;
  /** 업종 상세(주요 제품) · 법인 형태 — AI 판정 입력의 일부(해시) */
  product?: string; entity?: string;
  /** 「다시 판정」 — 업체 상세에서만(저장된 업체) · 끝나면 상위가 업체를 새로고침 */
  onJudge?: () => Promise<void>;
}
type AutoState = { subs: string[]; types: string[]; typesQ: string[]; src: 'ai' | 'text' | ''; stale: boolean; ai: { at?: string | null; conf: 'high' | 'mid' | 'low'; ev: Record<string, string>; fields: string[]; biz: string[]; note?: string } | null };
const AUTO0: AutoState = { subs: [], types: [], typesQ: [], src: '', stale: false, ai: null };
const CONF_TXT = { high: '확신 높음', mid: '확신 중간', low: '확신 낮음' } as const;
export default function CompanyMatchFields({ value: v, onChange, layout = 'full', companyName, govAi, product, entity, onJudge }: Props) {
  // 업종 글로 자동 해당하는 하위 업종 · 기업 유형 — 엔진(서버) 판정을 그대로 받아 칩에 미리 보여 준다 (2026-10-03 「고객 등록할 때 놓칠 것 같다」 · 2026-10-05g 하위 업종)
  // typesQ = 자동은 아니지만 확인이 필요한 유형(엔진 autoTags — 음식점업 · 숙박업이 둘째 업종이면 매장운영은 자동으로 넣지 않고 매장운영 공고에서 「확인 필요」 · 2026-10-07h)
  // 2026-10-07k 저장된 AI 판정(gov_ai)이 지금 글과 같으면 서버가 글 규칙 대신 그것을 돌려준다(src 'ai' · 근거 ev) — 글을 고치면 stale(글 규칙 미리 보기 · 저장하면 백그라운드가 다시 판정)
  const [auto, setAuto] = useState<AutoState>(AUTO0);
  const [judging, setJudging] = useState(false);
  const bizKey = v.biz_types.join(',');
  const aiKey = typeof govAi === 'string' ? govAi : JSON.stringify(govAi || null);
  useEffect(() => {
    const industry = v.biz_type.trim();
    if (!industry && !bizKey && !(companyName || '').trim()) { setAuto(AUTO0); return; }
    let dead = false;
    const t = window.setTimeout(() => {
      api.getGovAutoTags({ industry, product: product || '', biz_types: bizKey ? bizKey.split(',') : [], name: companyName || '', entity: entity || '', gov_ai: govAi || undefined })
        .then(r => {
          if (dead || !r.data) return;
          const d = r.data;   // typesQ 는 엔진이 있을 때만 싣는 키(없으면 키 없음)
          setAuto({ subs: (d.subs || []).filter(isSubKey), types: d.types || [], typesQ: (d.typesQ || []).filter(x => GOV_TYPES.includes(x)), src: d.src || 'text', stale: !!d.stale, ai: d.ai || null });
        })
        .catch(() => { /* 미리 보기라 실패해도 조용히 */ });
    }, 400);
    return () => { dead = true; window.clearTimeout(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v.biz_type, bizKey, companyName, product, entity, aiKey]);
  const aiEv = (k: string): string => (auto.src === 'ai' && auto.ai && auto.ai.ev[k]) ? `AI 판정 근거: «${auto.ai.ev[k]}»` : '';
  async function judgeNow() {
    if (!onJudge || judging) return;
    setJudging(true);
    try { await onJudge(); } finally { setJudging(false); }
  }
  const autoTypes = new Set(auto.types);
  const typesQ = new Set(auto.typesQ);
  // 하위 업종 — 자동(뺀 것 제외) + 직접 켠 키(arts 배열) · 끄면 자동은 sectors_off 로
  const subOn = (k: string) => v.arts.includes(k) || (auto.subs.includes(k) && !v.sectors_off.includes(k));
  const toggleSub = (k: string) => {
    if (subOn(k)) onChange({ arts: v.arts.filter(x => x !== k), ...(auto.subs.includes(k) && !v.sectors_off.includes(k) ? { sectors_off: [...v.sectors_off, k] } : {}) });
    else onChange({ arts: auto.subs.includes(k) ? v.arts.filter(x => x !== k) : [...v.arts, k], sectors_off: v.sectors_off.filter(x => x !== k) });
  };
  // 기업 유형 — 직접 체크(arts)는 그대로 · 자동(점선)은 누르면 «아님»(sectors_off) · «아님»은 누르면 다시 자동 · 나머지는 직접 체크 (2026-10-07h)
  const typeDropped = (t: string) => autoTypes.has(t) && !v.arts.includes(t) && v.sectors_off.includes(t);
  const toggleType = (t: string) => {
    if (v.arts.includes(t)) onChange({ arts: v.arts.filter(x => x !== t) });
    else if (autoTypes.has(t)) onChange({ sectors_off: v.sectors_off.includes(t) ? v.sectors_off.filter(x => x !== t) : [...v.sectors_off, t] });
    else onChange({ arts: [...v.arts, t], sectors_off: v.sectors_off.filter(x => x !== t) });   // 직접 켜면 묵은 «아님»은 지운다
  };
  // 하위 업종 상자를 보여 줄 업종 — 고른 업종 + 자동 · 직접 켠 키의 업종 (공통 분야 키는 업종 상자 밖)
  const keyParents = [...auto.subs, ...v.arts.filter(isSubKey)].map(k => parseSubKey(k)?.p || '').filter(p => p && p !== ANY_PARENT);
  const subParents = GOV_PARENTS.filter(p => GOV_SUBS[p].length > 0 && (v.biz_types.includes(p) || keyParents.includes(p)));
  const anyOn = v.arts.filter(k => parseSubKey(k)?.p === ANY_PARENT);
  const autoOnly = [...auto.subs.filter(k => !v.sectors_off.includes(k) && !v.arts.includes(k)).map(k => parseSubKey(k)?.s || k), ...auto.types.filter(x => !v.arts.includes(x) && !v.sectors_off.includes(x))];
  const droppedOnly = [...auto.subs.filter(k => !v.arts.includes(k) && v.sectors_off.includes(k)).map(k => parseSubKey(k)?.s || k), ...auto.types.filter(typeDropped)];
  const chipTitle = (x: string) => [GOV_SPECIAL_INFO[x]?.tip,
    typeDropped(x) ? DROPPED_TIP : autoTypes.has(x) && !v.arts.includes(x) ? '✓ 업종·이름으로 자동 해당 — 체크하지 않아도 매칭됩니다. 아니면 눌러서 «아님»으로 빼세요' : '',
    autoTypes.has(x) && !v.arts.includes(x) ? aiEv(x) : '',
    !autoTypes.has(x) && !v.arts.includes(x) && typesQ.has(x) ? '음식점업·숙박업이 둘째 업종이라 자동으로 넣지 않았습니다 — 매장을 운영하면 눌러서 체크하세요 (매장운영 전용 공고는 「확인 필요」로 나옵니다)' : ''].filter(Boolean).join('\n\n') || undefined;
  const prog = matchProgress(v);
  const done = prog.filled === prog.total;
  const cols = layout === 'compact' ? 2 : 1;
  return (
    <div>
      <style>{`.cmf-g2{display:grid;grid-template-columns:repeat(${cols},minmax(0,1fr));gap:10px}
        @media (max-width:520px){.cmf-g2{grid-template-columns:1fr!important}}`}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, padding: '9px 12px', borderRadius: R.sm, background: done ? C.greenLt : '#FFF7ED' }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: done ? C.green : '#B45309', whiteSpace: 'nowrap' }}>매칭 정보 {prog.filled}/{prog.total} 입력</span>
        <div style={{ flex: 1, height: 7, background: '#fff', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{ width: `${(prog.filled / prog.total) * 100}%`, height: '100%', background: done ? C.green : '#D97706', transition: 'width .2s' }} />
        </div>
      </div>
      <div className="cmf-g2" style={{ marginBottom: 14 }}>
        <div>
          <div style={lbl}>지역{need(!!v.region)}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {MATCH_REGIONS.map(r => <Pill key={r} active={v.region === r} bd="#EF9F27" bg="#FAEEDA" fg="#633806"
              onClick={() => onChange(v.region === r ? { region: '', region_detail: '' } : { region: r, ...((REGION_CITIES[r] || []).some(c => (v.region_detail || '').startsWith(c)) ? {} : { region_detail: '' }) })}>{r}</Pill>)}
          </div>
        </div>
        <div>
          <div style={lbl}>시·군·구 <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(선택 · 주소에서 자동)</span></div>
          {/* 2026-10-07g 지도사 「선택지가 나와야지」 — 글자 입력 대신 고른 지역의 시·군·구 칩(공고 양식과 같은 목록 REGION_CITIES · 하나만).
              주소로 자동 채운 값이 목록에 없으면(「청주시 흥덕구」 · 옛 표기) 그 값을 칩으로 보여 주고 누르면 지운다 */}
          {(() => {
            const cities = REGION_CITIES[v.region] || [];
            const cur = (v.region_detail || '').trim();
            const hit = cities.find(c => cur === c || cur.startsWith(c)) || '';
            if (!v.region) return <div style={{ fontSize: T.xs, color: '#9CA3AF', padding: '8px 0' }}>지역을 먼저 고르세요</div>;
            if (!cities.length) return <div style={{ fontSize: T.xs, color: '#9CA3AF', padding: '8px 0' }}>{v.region}은 시·군·구를 따로 고르지 않습니다</div>;
            return (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {cur && !hit && <Pill active bd="#EF9F27" bg="#FAEEDA" fg="#633806" title="주소에서 읽은 값 — 목록에 없음 · 누르면 지움" onClick={() => onChange({ region_detail: '' })}>{cur}</Pill>}
                {cities.map(c => <Pill key={c} active={hit === c} bd="#EF9F27" bg="#FAEEDA" fg="#633806"
                  onClick={() => onChange({ region_detail: hit === c ? '' : c })}>{c}</Pill>)}
              </div>
            );
          })()}
        </div>
      </div>
      <div style={{ marginBottom: 14 }}>
        <div style={lbl}>업종 <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(복수 선택)</span>{need(v.biz_types.length > 0 || v.biz_type.trim() !== '')}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 6 }}>
          {MATCH_BIZ_TYPES.map(bt => <Pill key={bt} active={v.biz_types.includes(bt)} bd="#AFA9EC" bg="#EEEDFE" fg="#3C3489"
            onClick={() => onChange({ biz_types: v.biz_types.includes(bt) ? v.biz_types.filter(t => t !== bt) : [...v.biz_types, bt] })}>{bt}</Pill>)}
        </div>
        <input style={inp} value={v.biz_type} placeholder="업종 자유 입력 (예: 정밀화학소재 제조업) — 세부업종 판정에 쓰입니다"
          onChange={e => onChange({ biz_type: e.target.value })} />
        {/* 2026-10-07k 세부업종 판정 출처 — AI(저장된 판정 · 근거는 칩에) / 글 규칙(판정 전 · 글이 바뀜) · 「다시 판정」 */}
        {auto.src === 'ai' && (   /* 2026-10-08 업체 AI 판정은 끔(공고만) — 예전에 저장된 판정이 있을 때만 출처를 보인다 */
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 6, fontSize: T.xs, color: auto.src === 'ai' ? '#1D4ED8' : '#9CA3AF' }}>
            {auto.src === 'ai' && auto.ai ? (
              <span title={auto.ai.note ? `AI 메모: ${auto.ai.note}` : undefined}>세부업종은 AI 가 업종 · 제품 글을 읽고 판정했습니다 · {CONF_TXT[auto.ai.conf]}{auto.ai.at ? ` · ${String(auto.ai.at).slice(0, 10)}` : ''}{auto.ai.fields.length ? ` · 분야 ${auto.ai.fields.join(' · ')}` : ''} — 칩에 마우스를 올리면 근거</span>
            ) : auto.stale ? (
              <span>업종 글이 바뀌어 AI 판정이 묵었습니다 — 지금은 글 규칙으로 보여 주고, 저장하면 다시 판정합니다</span>
            ) : (
              <span>AI 판정 전 — 글 규칙으로 보여 줍니다{onJudge ? '' : ' (저장하면 판정합니다)'}</span>
            )}
            {onJudge && <button type="button" onClick={judgeNow} disabled={judging} style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: '#fff', color: C.sub, cursor: judging ? 'default' : 'pointer' }}>{judging ? '판정 중…' : '다시 판정'}</button>}
          </div>
        )}
        {/* 세부업종 (2026-10-05g · 05q 업종 › 세부업종) — 업종 · 주요 제품 글로 자동(점선 「자동」) · 아니면 눌러서 빼기 · 안 잡힌 세부업종은 눌러서 켜기 */}
        {subParents.map(p => (
          <div key={p} style={{ marginTop: 8, padding: '8px 10px', borderRadius: R.sm, background: '#fff', border: '1px solid rgba(175,169,236,0.45)' }}>
            <div style={{ fontSize: T.xs, fontWeight: 600, color: '#3C3489', marginBottom: 6 }}>▾ {p} 세부업종 <span style={{ fontWeight: 400, color: '#9CA3AF' }}>({auto.src === 'ai' ? 'AI 판정으로 자동' : '업종·주요 제품으로 자동'} · 아니면 눌러서 빼기)</span></div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {GOV_SUBS[p].map(s => {
                const k = subKey(p, s);
                const isAuto = auto.subs.includes(k) && !v.arts.includes(k);
                const dropped = auto.subs.includes(k) && v.sectors_off.includes(k);
                const title = [GOV_SUB_INFO[s], isAuto && !dropped ? (auto.src === 'ai' ? '✓ AI 가 업종 · 제품 글을 읽고 해당으로 판정 — 아니면 눌러서 빼세요' : '✓ 업종으로 자동 해당 — 아니면 눌러서 빼세요') : '', isAuto ? aiEv(k) : '', dropped ? DROPPED_TIP : ''].filter(Boolean).join('\n\n') || undefined;
                return (
                  <Pill key={k} active={v.arts.includes(k)} auto={isAuto && !dropped} dropped={dropped} bd="#AFA9EC" bg="#EEEDFE" fg="#3C3489" title={title} onClick={() => toggleSub(k)}>
                    {dropped ? <span style={dropTxt}>{s}</span> : s}{isAuto && !dropped && <span style={autoMark}>자동</span>}{dropped && <span style={{ ...autoMark, opacity: 0.6 }}>뺌</span>}
                  </Pill>
                );
              })}
            </div>
          </div>
        ))}
        {v.biz_types.length > 0 && !subParents.length && !v.biz_type.trim() && (
          <div style={{ marginTop: 6, fontSize: T.xs, color: '#9CA3AF' }}>업종 글에 주요 제품을 적으면 세부업종이 자동으로 잡힙니다 (예: 선박 부품 제조 · 반려동물 사료 제조)</div>
        )}
        {anyOn.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 5, marginTop: 6, fontSize: T.xs, color: C.sub }}>
            <span>무관 세부업종 (예전 체크)</span>
            {anyOn.map(k => <Pill key={k} active bd="#AFA9EC" bg="#EEEDFE" fg="#3C3489" onClick={() => toggleSub(k)} title="누르면 뺍니다">무관 › {parseSubKey(k)?.s} ✕</Pill>)}
          </div>
        )}
      </div>
      <div className="cmf-g2" style={{ marginBottom: 14 }}>
        <div>
          <div style={lbl}>기업규모 <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(비우면 업종·매출·상시근로자로 자동 판정)</span>{need(!!v.company_scale || (v.employees !== '' && v.sales_eok !== ''))}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {MATCH_SCALES.map(s => <Pill key={s} active={v.company_scale === s} bd="#85B7EB" bg="#E6F1FB" fg="#0C447C"
              onClick={() => onChange({ company_scale: v.company_scale === s ? '' : s })}>{s}</Pill>)}
          </div>
        </div>
        <div>
          <div style={lbl}>창업기업 여부{need(v.is_startup !== null)}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {[{ l: '창업기업', b: true }, { l: '창업기업 아님', b: false }].map(o => (
              <Pill key={o.l} active={v.is_startup === o.b} bd="#85B7EB" bg="#E6F1FB" fg="#0C447C"
                onClick={() => onChange({ is_startup: v.is_startup === o.b ? null : o.b })}>{o.l}</Pill>
            ))}
          </div>
        </div>
      </div>
      <div className="cmf-g2" style={{ marginBottom: 14 }}>
        <div>
          <div style={lbl}>대표 생년월일{need(!!v.ceo_birth)}</div>
          <DateInput style={inp} value={v.ceo_birth} onChange={x => onChange({ ceo_birth: x })} placeholder="예: 19710101" aria-label="대표 생년월일" />
        </div>
        <div>
          <div style={lbl}>설립일{need(!!v.established_date)}</div>
          <DateInput style={inp} value={v.established_date} onChange={x => onChange({ established_date: x })} placeholder="예: 20200317" aria-label="설립일" />
        </div>
      </div>
      <div className="cmf-g2" style={{ marginBottom: 14 }}>
        <div>
          <div style={lbl}>상시근로자수 <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(명)</span>{need(v.employees !== '')}</div>
          <input type="number" min={0} style={inp} value={v.employees} placeholder="예: 5" onChange={e => onChange({ employees: e.target.value })} />
        </div>
        <div>
          <div style={lbl}>최근 연매출 <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(억원)</span>{need(v.sales_eok !== '')}</div>
          <input type="number" min={0} step="0.1" style={inp} value={v.sales_eok} placeholder="예: 12" onChange={e => onChange({ sales_eok: e.target.value })} />
        </div>
      </div>
      <div style={{ marginBottom: 14 }}>
        <div style={lbl}>관심 분류 <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(복수 선택 · 하나라도 겹치면 매칭)</span>{need(v.tags.length > 0)}</div>
        <GovCategoryPicker compact value={v.tags} onChange={(next) => onChange({ tags: next })} />
      </div>
      <div>
        {(autoOnly.length > 0 || droppedOnly.length > 0) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 10, padding: '8px 11px', borderRadius: R.sm, background: autoOnly.length > 0 ? C.greenLt : '#F3F4F6', fontSize: T.xs, color: C.green, lineHeight: 1.5 }}>
            {autoOnly.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: '2px 6px' }}>
                <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>✓ 자동 해당</span>
                <span style={{ color: C.text }}><b>{autoOnly.join(' · ')}</b> <span style={{ color: C.sub }}>— 업종{(companyName || '').trim() ? '·이름' : ''}으로 판정돼 체크하지 않아도 그 세부업종·유형 전용 공고에 매칭됩니다 (점선 칩)</span></span>
              </div>
            )}
            {/* «아님» 안내 (2026-10-07h) — 자동이 틀렸으면 점선 칩을 눌러 뺀다 · 뺀 것은 흐린 칩으로 남아 다시 누르면 돌아온다 */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: '2px 6px', color: C.sub }}>
              <span style={{ fontWeight: 700, whiteSpace: 'nowrap', color: '#6B7280' }}>✕ 아님</span>
              <span>{droppedOnly.length > 0 ? <><b style={{ color: '#6B7280', textDecoration: 'line-through' }}>{droppedOnly.join(' · ')}</b> — «아님»으로 뺐습니다(매칭에서 제외) · 다시 넣으려면 그 칩을 누르세요</> : '자동 판정이 틀렸으면 점선 칩을 눌러 «아님»으로 빼세요 — 그 유형·세부업종 전용 공고에 매칭되지 않습니다'}</span>
            </div>
          </div>
        )}
        <div style={{ fontSize: 12, fontWeight: 600, color: C.sub, marginBottom: 2 }}>전용 대상 <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(업종으로 알 수 없는 것만 · 복수 선택)</span></div>
        <div style={{ fontSize: T.xs, color: '#9CA3AF', marginBottom: 8 }}>{TARGET_GUIDE}</div>
        {/* 한 목록 (2026-10-05h) — 기업 유형 · 특수 자격 모두 업체 tags. 재창업 · 재해 · 넷제로는 체크된 경우에만 연결
            자동(점선) 유형 칩 → 누르면 «아님»(sectors_off · 흐린 취소선 「뺌」) → 다시 누르면 자동 (2026-10-07h) · 직접 체크한 칩은 누르면 해제 */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {[...COMPANY_TYPES, ...GOV_SPECIAL_Q].map(t => {
            const on = v.arts.includes(t), dropped = typeDropped(t), isAuto = autoTypes.has(t) && !on && !dropped, ask = !on && !dropped && !autoTypes.has(t) && typesQ.has(t);
            return (
              <Pill key={t} active={on} auto={isAuto} dropped={dropped} bd="#7FB8A8" bg="#E6F4EF" fg="#0F5A47" title={chipTitle(t)} onClick={() => toggleType(t)}>
                {dropped ? <span style={dropTxt}>{t}</span> : t}{GOV_SPECIAL_INFO[t] && <span style={subTxt}>{GOV_SPECIAL_INFO[t].sub}</span>}
                {isAuto && <span style={autoMark}>자동</span>}{dropped && <span style={{ ...autoMark, opacity: 0.6 }}>뺌</span>}{ask && <span style={{ ...autoMark, color: '#B45309', opacity: 1 }}>확인</span>}
              </Pill>
            );
          })}
        </div>
      </div>
    </div>
  );
}